import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge, Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Modal, Select, Spinner, StatusBadge, StatTile,
} from '../components/ui';
import DilutionBars from '../components/charts/DilutionBars';
import { date, enumLabel, localToday, money, percent, pricePerShare, shares } from '../lib/format';

export default function Rounds() {
  const { companyId, role } = useCompany();
  const [modelling, setModelling] = useState(false);
  const [creating, setCreating] = useState(false);
  const [action, setAction] = useState(null); // {kind: 'close' | 'investments', round}
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState(null);

  const list = useAsync(() => api.rounds(companyId), [companyId]);
  const canEdit = ['LEGAL', 'ADMIN', 'OWNER'].includes(role);
  // Closing is stricter than creating: it issues stock and converts every
  // outstanding SAFE/note atomically, so the API itself requires ADMIN, not LEGAL.
  const canClose = ['ADMIN', 'OWNER'].includes(role);

  async function cancelRound(round) {
    if (!window.confirm(`Cancel ${round.name}? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.cancelRound(companyId, round.id);
      setFlash(`Cancelled ${round.name}.`);
      list.reload();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Rounds</h1>
          <p>
            Model a priced round before you sign anything — the modeller runs the same
            conversion code that closing runs, so the preview cannot disagree with the outcome.
          </p>
        </div>
        <div className="page-actions">
          <Button onClick={() => setModelling(true)}>Model a round</Button>
          {canEdit ? (
            <Button variant="primary" onClick={() => setCreating(true)}>New round</Button>
          ) : null}
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />
      <ErrorBanner error={error} />

      {list.loading ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBanner error={list.error} onRetry={list.reload} />
      ) : (
        <Card padded={false}>
          <DataTable
            rowKey={(r) => r.id}
            empty="No rounds yet."
            columns={[
              { key: 'name', header: 'Round', render: (r) => <span className="primary-cell">{r.name}</span> },
              { key: 'type', header: 'Type', render: (r) => r.round_type ?? '—' },
              { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              { key: 'pps', header: 'Price/share', align: 'right', numeric: true, render: (r) => (r.price_per_share ? pricePerShare(r.price_per_share) : '—') },
              { key: 'pre', header: 'Pre-money', align: 'right', numeric: true, render: (r) => (r.pre_money_valuation ? money(r.pre_money_valuation, { places: 0 }) : '—') },
              { key: 'raised', header: 'Raised', align: 'right', numeric: true, render: (r) => money(r.amount_raised, { places: 0 }) },
              { key: 'sharesIssued', header: 'Shares', align: 'right', numeric: true, render: (r) => shares(r.shares_issued) },
              // A column with no `render` falls back to row[key], and the query
              // returns investor_count — so `investors` rendered blank.
              { key: 'investors', header: 'Investors', align: 'right', numeric: true, render: (r) => r.investor_count ?? 0 },
              { key: 'closed', header: 'Closed', render: (r) => date(r.close_date) },
              {
                key: 'actions',
                header: '',
                render: (r) => {
                  if (r.status === 'CLOSED') {
                    return (
                      <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'investments', round: r })}>
                        View investments
                      </Button>
                    );
                  }
                  if (r.status === 'DRAFT' || r.status === 'OPEN') {
                    return (
                      <div style={{ display: 'flex', gap: 4 }}>
                        {canEdit ? (
                          <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'edit', round: r })}>
                            Edit
                          </Button>
                        ) : null}
                        {canClose ? (
                          <Button size="sm" variant="primary" onClick={() => setAction({ kind: 'close', round: r })}>
                            Close round
                          </Button>
                        ) : null}
                        {canEdit ? (
                          <Button size="sm" variant="ghost" onClick={() => cancelRound(r)}>
                            Cancel round
                          </Button>
                        ) : null}
                      </div>
                    );
                  }
                  return null;
                },
              },
            ]}
            rows={list.data}
          />
        </Card>
      )}

      {modelling ? <ModelRoundModal companyId={companyId} onClose={() => setModelling(false)} /> : null}
      {creating ? (
        <NewRoundModal
          companyId={companyId}
          onClose={() => setCreating(false)}
          onSaved={(name) => { setCreating(false); setFlash(`Created ${name}.`); list.reload(); }}
        />
      ) : null}
      {action?.kind === 'edit' ? (
        <EditRoundModal
          companyId={companyId}
          round={action.round}
          onClose={() => setAction(null)}
          onSaved={(msg) => { setAction(null); setFlash(msg); list.reload(); }}
        />
      ) : null}
      {action?.kind === 'close' ? (
        <CloseRoundModal
          companyId={companyId}
          round={action.round}
          onClose={() => setAction(null)}
          onClosed={(msg) => { setAction(null); setFlash(msg); list.reload(); }}
        />
      ) : null}
      {action?.kind === 'investments' ? (
        <InvestmentsModal companyId={companyId} round={action.round} onClose={() => setAction(null)} />
      ) : null}
    </div>
  );
}

function ModelRoundModal({ companyId, onClose }) {
  const [inputs, setInputs] = useState({ preMoneyValuation: '', pricePerShare: '', newMoney: '', poolIncrease: '' });
  const [result, setResult] = useState(null);
  const [lastParams, setLastParams] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setInputs((f) => ({ ...f, [k]: e.target.value }));

  async function run(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = Object.fromEntries(Object.entries(inputs).filter(([, v]) => v !== ''));
      setResult(await api.modelRound(companyId, body));
      setLastParams(body);
    } catch (err) {
      setError(err);
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  // Re-downloads the exact scenario shown, not whatever is currently typed
  // into the form -- lastParams is only ever set alongside a result.
  async function download() {
    setDownloading(true);
    setError(null);
    try {
      await api.exportModelRound(companyId, lastParams);
    } catch (err) {
      setError(err);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Modal title="Model a priced round" wide onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}>
      <ErrorBanner error={error} />
      <form onSubmit={run}>
        <div className="form-grid">
          <Field label="Pre-money valuation" hint="Or give a price per share instead">
            <Input value={inputs.preMoneyValuation} onChange={set('preMoneyValuation')} inputMode="decimal" />
          </Field>
          <Field label="Price per share">
            <Input value={inputs.pricePerShare} onChange={set('pricePerShare')} inputMode="decimal" />
          </Field>
          <Field label="New money">
            <Input value={inputs.newMoney} onChange={set('newMoney')} inputMode="decimal" />
          </Field>
          <Field label="Option pool increase" hint="Comes out of the pre-money — it dilutes existing holders, not the incoming investor">
            <Input value={inputs.poolIncrease} onChange={set('poolIncrease')} inputMode="numeric" />
          </Field>
        </div>
        <Button type="submit" variant="primary" loading={busy}>Model it</Button>
      </form>

      {result ? (
        <div className="stack" style={{ marginTop: 20 }}>
          <div className="grid grid-stats">
            <StatTile label="Price per share" value={pricePerShare(result.pricing.pricePerShare)} />
            <StatTile label="Pre-round FD" value={shares(result.totals.preRoundFullyDiluted)} />
            <StatTile label="Post-round FD" value={shares(result.totals.postRoundFullyDiluted)} />
            <StatTile label="New money shares" value={shares(result.totals.newMoneyShares)} />
            <StatTile label="From conversions" value={shares(result.totals.convertedShares)} />
          </div>

          <div>
            <Button loading={downloading} onClick={download}>Download as spreadsheet</Button>
          </div>

          {result.conversion.conversions?.length ? (
            <Card title="How the convertibles convert" padded={false}>
              <DataTable
                rowKey={(r) => r.securityId}
                columns={[
                  { key: 'holder', header: 'Holder', render: (r) => r.stakeholderName },
                  { key: 'amount', header: 'Amount', align: 'right', numeric: true, render: (r) => money(r.amountConverted, { places: 0 }) },
                  { key: 'basis', header: 'Binding term', render: (r) => enumLabel(r.conversionBasis) },
                  { key: 'price', header: 'Conv. price', align: 'right', numeric: true, render: (r) => pricePerShare(r.conversionPrice) },
                  { key: 'shares', header: 'Shares', align: 'right', numeric: true, render: (r) => shares(r.shares) },
                  { key: 'pct', header: '% post', align: 'right', numeric: true, render: (r) => percent(r.percentPostRound) },
                ]}
                rows={result.conversion.conversions}
              />
            </Card>
          ) : null}

          <Card title="Dilution">
            <DilutionBars rows={result.dilution} />
          </Card>
        </div>
      ) : null}
    </Modal>
  );
}

// Fields a caller can clear back to "unset" -- omitting them from the PATCH
// body would leave the existing value untouched, which is the wrong result
// when the user has visibly emptied the input. option_pool_increase is not
// here: the column is NOT NULL with a default of 0, so an emptied field
// means "reset to zero," not "clear."
const ROUND_NULLABLE_FIELDS = ['round_type', 'share_class_id', 'pre_money_valuation', 'price_per_share', 'target_amount'];

function EditRoundModal({ companyId, round, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: round.name ?? '',
    round_type: round.round_type ?? '',
    share_class_id: round.share_class_id ?? '',
    pre_money_valuation: round.pre_money_valuation ?? '',
    price_per_share: round.price_per_share ?? '',
    target_amount: round.target_amount ?? '',
    option_pool_increase:
      round.option_pool_increase && round.option_pool_increase !== '0' ? round.option_pool_increase : '',
    status: round.status,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const classes = useAsync(() => api.shareClasses(companyId), [companyId]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {};
      for (const [key, value] of Object.entries(form)) {
        if (value !== '') {
          body[key] = value;
        } else if (ROUND_NULLABLE_FIELDS.includes(key)) {
          body[key] = null;
        } else if (key === 'option_pool_increase') {
          body[key] = '0';
        }
      }
      await api.updateRound(companyId, round.id, body);
      onSaved(`Updated ${form.name}.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Edit round" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Save</Button></>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Name" required><Input value={form.name} onChange={set('name')} required autoFocus /></Field>
          <Field label="Round type" hint="e.g. Series A"><Input value={form.round_type} onChange={set('round_type')} /></Field>
          <Field label="Status" hint="Closing or cancelling happens through their own actions, not here">
            <Select value={form.status} onChange={set('status')}>
              <option value="DRAFT">Draft</option>
              <option value="OPEN">Open</option>
            </Select>
          </Field>
          <Field label="Preferred class">
            <Select value={form.share_class_id} onChange={set('share_class_id')}>
              <option value="">Decide at close</option>
              {(classes.data ?? []).filter((c) => c.class_type === 'PREFERRED').map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Pre-money valuation"><Input value={form.pre_money_valuation} onChange={set('pre_money_valuation')} inputMode="decimal" /></Field>
          <Field label="Price per share"><Input value={form.price_per_share} onChange={set('price_per_share')} inputMode="decimal" /></Field>
          <Field label="Target amount"><Input value={form.target_amount} onChange={set('target_amount')} inputMode="decimal" /></Field>
          <Field label="Pool increase"><Input value={form.option_pool_increase} onChange={set('option_pool_increase')} inputMode="numeric" /></Field>
        </div>
      </form>
    </Modal>
  );
}

function NewRoundModal({ companyId, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', roundType: '', shareClassId: '', preMoneyValuation: '', pricePerShare: '', targetAmount: '', optionPoolIncrease: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const classes = useAsync(() => api.shareClasses(companyId), [companyId]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
      await api.createRound(companyId, body);
      onSaved(form.name);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="New round" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Create</Button></>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Name" required><Input value={form.name} onChange={set('name')} required autoFocus /></Field>
          <Field label="Round type" hint="e.g. Series A"><Input value={form.roundType} onChange={set('roundType')} /></Field>
          <Field label="Preferred class">
            <Select value={form.shareClassId} onChange={set('shareClassId')}>
              <option value="">Decide at close</option>
              {(classes.data ?? []).filter((c) => c.class_type === 'PREFERRED').map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Pre-money valuation"><Input value={form.preMoneyValuation} onChange={set('preMoneyValuation')} inputMode="decimal" /></Field>
          <Field label="Price per share"><Input value={form.pricePerShare} onChange={set('pricePerShare')} inputMode="decimal" /></Field>
          <Field label="Target amount"><Input value={form.targetAmount} onChange={set('targetAmount')} inputMode="decimal" /></Field>
          <Field label="Pool increase"><Input value={form.optionPoolIncrease} onChange={set('optionPoolIncrease')} inputMode="numeric" /></Field>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>
          Closing a round is a separate, atomic step: it fixes the price, converts every
          outstanding SAFE and note, records the pool increase and issues preferred — all or nothing.
        </p>
      </form>
    </Modal>
  );
}

/**
 * The one genuinely hard write in the product: fixes the price, converts every
 * outstanding SAFE/note, issues preferred to each new-money investor below, and
 * records the pool increase — all in one transaction, or none of it (rounds.js).
 */
function CloseRoundModal({ companyId, round, onClose, onClosed }) {
  const today = localToday();
  const refs = useAsync(
    () =>
      Promise.all([api.stakeholders(companyId), api.shareClasses(companyId), api.equityPlans(companyId)]).then(
        ([stakeholders, shareClasses, plans]) => ({ stakeholders, shareClasses, plans })
      ),
    [companyId]
  );

  const [form, setForm] = useState({
    closeDate: today,
    shareClassId: round.share_class_id ?? '',
    pricePerShare: round.price_per_share ?? '',
    preMoneyValuation: round.pre_money_valuation ?? '',
    optionPoolIncrease:
      round.option_pool_increase && round.option_pool_increase !== '0' ? round.option_pool_increase : '',
    equityPlanId: '',
  });
  const [investments, setInvestments] = useState([{ stakeholderId: '', amount: '', label: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fe = error?.fieldErrors ?? {};

  const setInvestment = (index, key) => (e) =>
    setInvestments((rows) => rows.map((r, i) => (i === index ? { ...r, [key]: e.target.value } : r)));
  const addInvestment = () => setInvestments((rows) => [...rows, { stakeholderId: '', amount: '', label: '' }]);
  const removeInvestment = (index) => setInvestments((rows) => rows.filter((_, i) => i !== index));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        closeDate: form.closeDate,
        investments: investments
          .filter((inv) => inv.stakeholderId && inv.amount)
          .map((inv) => ({
            stakeholderId: inv.stakeholderId,
            amount: inv.amount,
            ...(inv.label ? { label: inv.label } : {}),
          })),
      };
      if (form.shareClassId) body.shareClassId = form.shareClassId;
      if (form.pricePerShare) body.pricePerShare = form.pricePerShare;
      else if (form.preMoneyValuation) body.preMoneyValuation = form.preMoneyValuation;
      if (form.optionPoolIncrease) {
        body.optionPoolIncrease = form.optionPoolIncrease;
        if (form.equityPlanId) body.equityPlanId = form.equityPlanId;
      }

      const result = await api.closeRound(companyId, round.id, body);
      const converted = result.conversion?.conversions?.length ?? 0;
      const issued = result.issued?.length ?? 0;
      onClosed(
        `Closed ${result.round.name} at ${pricePerShare(result.round.price_per_share)}/share — ` +
          `${converted} convertible${converted === 1 ? '' : 's'} converted, ` +
          `${issued} position${issued === 1 ? '' : 's'} issued.`
      );
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const preferredClasses = (refs.data?.shareClasses ?? []).filter((c) => c.class_type === 'PREFERRED');

  return (
    <Modal
      title={`Close ${round.name}`}
      wide
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Close round</Button></>}
    >
      <ErrorBanner error={error} />
      <p className="muted" style={{ fontSize: 13 }}>
        Atomic and not reversible from here: it fixes the price, converts every outstanding SAFE
        and note, issues preferred to each investor below, and records any pool increase — all in
        one step, or none of it.
      </p>
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Close date" required>
            <Input type="date" value={form.closeDate} onChange={set('closeDate')} required />
          </Field>
          <Field label="Preferred class" required={!round.share_class_id} error={fe.shareClassId}>
            <Select value={form.shareClassId} onChange={set('shareClassId')}>
              <option value="">Choose…</option>
              {preferredClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Price per share" hint="Or give a pre-money valuation instead" error={fe.pricePerShare}>
            <Input value={form.pricePerShare} onChange={set('pricePerShare')} inputMode="decimal" />
          </Field>
          <Field label="Pre-money valuation">
            <Input value={form.preMoneyValuation} onChange={set('preMoneyValuation')} inputMode="decimal" />
          </Field>
          <Field label="Option pool increase" hint="Comes out of the pre-money, dilutes existing holders">
            <Input value={form.optionPoolIncrease} onChange={set('optionPoolIncrease')} inputMode="numeric" />
          </Field>
          {form.optionPoolIncrease ? (
            <Field label="Plan for the increase" hint="Defaults to the company's first plan if left blank">
              <Select value={form.equityPlanId} onChange={set('equityPlanId')}>
                <option value="">Default</option>
                {(refs.data?.plans ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
          ) : null}
        </div>

        <h3 style={{ fontSize: 14, marginTop: 16 }}>New-money investors</h3>
        <p className="muted" style={{ fontSize: 12 }}>
          Every outstanding SAFE and note converts automatically at this price — list only the
          investors putting in new money at this close.
        </p>
        {investments.map((inv, index) => (
          <div key={index} className="form-grid" style={{ alignItems: 'flex-end' }}>
            <Field label="Investor">
              <Select value={inv.stakeholderId} onChange={setInvestment(index, 'stakeholderId')}>
                <option value="">Choose…</option>
                {(refs.data?.stakeholders ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
            <Field label="Amount">
              <Input value={inv.amount} onChange={setInvestment(index, 'amount')} inputMode="decimal" />
            </Field>
            <Field label="Certificate label" hint="Auto-generated if left blank">
              <Input value={inv.label} onChange={setInvestment(index, 'label')} />
            </Field>
            <Button type="button" variant="ghost" onClick={() => removeInvestment(index)}>Remove</Button>
          </div>
        ))}
        <Button type="button" onClick={addInvestment}>Add investor</Button>
      </form>
    </Modal>
  );
}

/** Read-only: who ended up with what once a round closed, including which
    positions came from a converted SAFE/note rather than new money. */
function InvestmentsModal({ companyId, round, onClose }) {
  const detail = useAsync(() => api.round(companyId, round.id), [companyId, round.id]);

  return (
    <Modal title={`${round.name} — investments`} wide onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      {detail.loading ? (
        <Spinner />
      ) : detail.error ? (
        <ErrorBanner error={detail.error} onRetry={detail.reload} />
      ) : (
        <DataTable
          rowKey={(r) => r.id}
          empty="No investments recorded."
          columns={[
            { key: 'holder', header: 'Investor', render: (r) => <span className="primary-cell">{r.stakeholder_name}</span> },
            { key: 'amount', header: 'Amount', align: 'right', numeric: true, render: (r) => money(r.amount, { places: 0 }) },
            { key: 'shares', header: 'Shares', align: 'right', numeric: true, render: (r) => shares(r.shares) },
            { key: 'security', header: 'Certificate', render: (r) => r.security_label ?? '—' },
            { key: 'conversion', header: '', render: (r) => (r.from_conversion ? <Badge tone="info">converted</Badge> : null) },
          ]}
          rows={detail.data.investments}
        />
      )}
    </Modal>
  );
}
